<script lang="ts">
	import { format } from 'src/services/i18n';
	import { Button, ButtonV2, Text, toast } from '@Shelly/shelly-design-system';
	import type { Device } from 'src/types/Device';
	import FirmwareUpdate from './FirmwareUpdate.svelte';
	import SettingGuide from '../../SettingGuide.svelte';
	import ModalPrompt from 'src/components/ModalPrompt.svelte';
	import FactoryResetModal from 'src/components/FactoryResetModal.svelte';
	import DeleteDeviceModal from './DeviceInfo/DeleteDeviceModal.svelte';
	import BatchTransferModal from 'src/components/BatchCommandModals/BatchTransferModal.svelte';
	import SharedHomeOwnerManagedAlert from 'src/components/SharedHome/SharedHomeOwnerManagedAlert.svelte';
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		get_local_ip,
		isWallDisplay,
		is_GBLE,
		is_gen_2,
		is_loqed,
		real_id,
	} from 'src/helpers/interface_helpers';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { localReq, postReq } from 'src/services/http';
	import { rebooting_devices } from 'src/stores/device-runtime/process';
	import { rebootRequired } from 'src/stores/device-runtime/states';
	import { getDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { getFactoryResetSuccessToastTitle } from 'src/helpers/device_settings_helpers';
	import { resetDrawerClose } from 'src/helpers/drawer_helpers';
	import { deleteDevice as deleteDeviceHelper } from 'src/helpers/add_delete_helpers';
	import { deleteWallDisplayExtention } from 'src/helpers/wall_display_extract/extracting_helpers';
	import { devices } from 'src/stores/lists/devices';
	import { rooms } from 'src/stores/lists/rooms';
	import { homes, unitOptions } from 'src/stores/account/home';
	import { user_data } from 'src/stores/account/user';
	import { profiles } from 'src/stores/features/profiles';
	import { update } from 'src/stores/ui/edit_lists';
	import { routeToDeviceSettings } from 'src/stores/ui/route-to-device-settings';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { SLEEPING_TYPES } from 'src/constants';
	import { devices_config } from '@devices/index';
	import { getXT1DeviceName, isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';
	import type { DeviceUsage } from './DeviceInfo/device_usage_helper';
	import {
		getAlarmsWithDevice,
		getDashboardsWithDevice,
		getGroupsWithDevice,
		getScenesWithDevice,
		getThermostatsWithDevice,
	} from './DeviceInfo/device_usage_helper';

	export let device: Device;
	export let isOffline = false;
	export let useNewLayout = false;
	export let actionTitles: string[] = [];
	export let showFirmware = false;

	$: ButtonC = useNewLayout ? ButtonV2 : Button;

	type ButtonVariant =
		| 'primary'
		| 'secondary'
		| 'danger'
		| 'danger-secondary'
		| 'premium'
		| 'success'
		| 'custom';

	const ACTION_BUTTON_CONFIG: Record<string, { icon: string; variant: ButtonVariant }> = {
		device_reboot: { icon: 'fas fa-arrows-rotate', variant: 'secondary' },
		soft_reboot: { icon: 'fas fa-arrows-rotate', variant: 'secondary' },
		factory_reset: { icon: 'fas fa-rotate-left', variant: 'secondary' },
		factory_reset_g1: { icon: 'fas fa-rotate-left', variant: 'secondary' },
		factory_reset_ble: { icon: 'fas fa-rotate-left', variant: 'secondary' },
		factory_reset_z: { icon: 'fas fa-rotate-left', variant: 'secondary' },
		transfer_device: { icon: 'fas fa-people-arrows', variant: 'secondary' },
		delete_device: { icon: 'fas fa-trash', variant: 'danger' },
	};

	const ACTION_ITEM_ORDER: Record<string, number> = {
		device_reboot: 0,
		soft_reboot: 1,
		factory_reset: 2,
		factory_reset_g1: 2,
		factory_reset_ble: 2,
		factory_reset_z: 2,
		transfer_device: 3,
		delete_device: 4,
	};

	const WALL_DISPLAY_REBOOT_GUIDE =
		'Reboot restarts the whole Wall Display and can take up to 2 minutes. Soft reboot only restarts the screen app, which is faster — try it first if the display is stuck or unresponsive.';

	$: isSharedHomeActive = isActiveHomeShared($homes.list);
	$: isGBLE = is_GBLE(device);
	$: statusStore = getDeviceStatus(device?.id);
	$: rebootPending =
		Boolean(($statusStore as { sys?: { restart_required?: boolean } })?.sys?.restart_required) ||
		$rebootRequired?.id === device?.id;

	$: orderedActionTitles = [...actionTitles].sort(
		(a, b) => (ACTION_ITEM_ORDER[a] ?? 99) - (ACTION_ITEM_ORDER[b] ?? 99),
	);

	let showRebootModal = false;
	let showSoftRebootModal = false;
	let showFactoryResetModal = false;
	let showDeleteModal = false;
	let showTransferModal = false;
	let deleteDeviceIsUsed = false;
	let deleteDeviceUsage: DeviceUsage = {
		room: [],
		group: [],
		alarm: [],
		scene: [],
		dashboard: [],
		thermostat: [],
	};

	const rebootDevice = async () => {
		const id = real_id(device.id);
		const gen2 = is_gen_2(device);
		const localIp = get_local_ip(id);
		let res;
		if (gen2) {
			res = await gen2Request(device, 'Shelly.Reboot', {});
		} else if (localIp) {
			res = await localReq(device, `${localIp}/reboot`, 'post', {});
		} else {
			res = await postReq(`/device/reboot`, { id });
		}
		if (res.isok) {
			toast.open({ type: 'success', title: $format('reboot_scc_toast') });
			rebooting_devices.update(id);
		} else {
			toast.open({ type: 'danger', title: $format('reboot_err_toast') });
		}
	};

	$: rebootProps = {
		title: $format('reboot_device_title', { default: 'Reboot Device?' }),
		text: $format('reboot_device_info_text', {
			default:
				'Rebooting the device will restart its operating system, potentially resolving minor issues and improving overall performance. This action will briefly interrupt device functionality.',
		}),
		button_2: $format('button_cancel'),
		button_1: $format('button_reboot_device', { default: 'Reboot device' }),
		callback: rebootDevice,
	};

	const softRebootDevice = async () => {
		const id = real_id(device.id);
		const res = await gen2Request(device, 'Sys.RestartApplication', {});
		if (res.isok) {
			toast.open({
				type: 'success',
				title: $format('soft_reboot_scc_toast', {
					default: 'Your device will perform software reboot after 5 seconds.',
				}),
			});
			rebooting_devices.update(id);
		} else {
			toast.open({ type: 'danger', title: $format('reboot_err_toast') });
		}
	};

	$: softRebootProps = {
		title: capFirstChar(
			`${$format('modal_title_soft_reboot', {
				default: 'Soft Reboot',
			})} ${$format('process_device')}?`,
		),
		text: $format('soft_reboot_device_confirmation_msg', {
			default:
				'A soft reboot will restart the device’s operating system, which can help resolve minor issues and improve performance.',
		}),
		button_2: $format('button_cancel'),
		button_1: $format('button_soft_reboot', { default: 'soft reboot' }),
		callback: softRebootDevice,
	};

	const factoryReset = async () => {
		const id = real_id(device.id);
		const gen2 = is_gen_2(device);
		const localIp = get_local_ip(id);
		let res;
		if (!gen2 && !isGBLE) {
			if (localIp) {
				res = await localReq(device, `${localIp}/settings`, 'post', { reset: 1 });
			} else {
				res = await postReq(`/device/factory_reset?id=${id}`, { id });
			}
		} else {
			res = await gen2Request(device, 'Shelly.FactoryReset', {}, undefined, undefined, 12000);
		}
		if (res.isok) {
			resetDrawerClose();
			toast.open({
				type: 'success',
				title: getFactoryResetSuccessToastTitle(device),
				duration: 10000,
			});
			if (isWallDisplay(device.type)) {
				await deleteWallDisplayExtention($devices[real_id(device.id)]);
				return;
			}
			await deleteDeviceHelper(id);
		} else if (res?.errors?.code == 500) {
			await deleteDeviceHelper(id);
		} else {
			toast.open({ type: 'danger', title: $format('factory_reset_err_toast') });
		}
	};

	$: factoryResetProps = {
		title: `${$format('reset_device_to_factory_stngs', {
			default: 'Reset Device to Factory Settings',
		})}?`,
		name: device?.name,
		text: $format('resetting_device_msg', {
			values: { device_name: device.name },
			default: `Resetting the device '{device_name}' to its factory settings will erase all data and settings, including its network configuration. To reconnect, use its Access Point network.`,
		}),
		alert: {
			heading: $format('resseting_device_alert_msg', {
				default:
					'Resetting the device to factory settings will also delete the device from the app.',
			}),
			type: 'danger' as const,
		},
		button_1: $format('button_reset_device', { default: 'Reset device' }),
		button_1_variant: 'danger' as const,
		button_2: $format('button_cancel'),
		callback: factoryReset,
		isSleepingDevice: SLEEPING_TYPES.includes(device.type),
	};

	const onDeleteDeviceAction = () => {
		deleteDeviceUsage = {
			room: $rooms[device.room_id] !== undefined ? [$rooms[device.room_id]] : [],
			group: getGroupsWithDevice(device.id),
			alarm: getAlarmsWithDevice(device.id),
			scene: getScenesWithDevice(device.id),
			dashboard: getDashboardsWithDevice(device.id),
			thermostat: getThermostatsWithDevice(device.id),
		} as DeviceUsage;
		deleteDeviceIsUsed = Object.values(deleteDeviceUsage).some((array) => array.length > 0);
		showDeleteModal = true;
	};

	const onTransferDeviceAction = () => {
		if ($profiles.length > 1 || $unitOptions.length > 0) {
			update.update(() => ({
				value: true,
				subType: '',
				selectAll: false,
				type: 'account_transfer_devices',
				selectedDevices: { [device.id]: device } as any,
			}));
			showTransferModal = true;
		} else {
			toast.open({
				type: 'info',
				title: $format('alert_no_switchable_accounts_or_other_homes', {
					default: 'You have no switchable accounts or other homes to transfer your devices.',
				}),
			});
		}
	};

	const onActionClick = (title: string) => {
		if (title === 'delete_device') {
			onDeleteDeviceAction();
		} else if (title === 'device_reboot') {
			showRebootModal = true;
		} else if (title === 'soft_reboot') {
			showSoftRebootModal = true;
		} else if (title === 'factory_reset') {
			if (isGBLE) {
				routeToDeviceSettings.setTarget(device.id, 'settings', title);
			} else {
				showFactoryResetModal = true;
			}
		} else if (title === 'transfer_device') {
			onTransferDeviceAction();
		} else {
			routeToDeviceSettings.setTarget(device.id, 'settings', title);
		}
	};

	function getActionLabel(title: string): string {
		if (title === 'delete_device' && device.shared) {
			return capFirstChar($format('button_remove_device', { default: 'Remove device' }));
		}
		return capFirstChar(
			$format(`sub_settings_title_${title}`, {
				default: title.replace(/_/g, ' '),
			}),
		);
	}

	function getActionButtonConfig(title: string): { icon: string; variant: ButtonVariant } {
		return ACTION_BUTTON_CONFIG[title] ?? { icon: 'fas fa-cog', variant: 'secondary' };
	}

	$: showTransfer =
		orderedActionTitles.includes('transfer_device') &&
		!device.shared &&
		!device.shared_by_me &&
		!device.id.includes('_') &&
		!isWallDisplay(device.type) &&
		!is_loqed(device) &&
		!$user_data?.installer;

	$: visibleActionTitles = orderedActionTitles.filter(
		(title) => title !== 'transfer_device' || showTransfer,
	);

	$: hasWallDisplayRebootPair =
		isWallDisplay(device.type) &&
		visibleActionTitles.includes('device_reboot') &&
		visibleActionTitles.includes('soft_reboot');

	function isWallDisplayRebootPairTitle(title: string): boolean {
		return hasWallDisplayRebootPair && (title === 'device_reboot' || title === 'soft_reboot');
	}

	$: deviceTypeName = isXT1Module(device?.type)
		? getXT1DeviceName(device)
		: devices_config[device?.type]?.friendly_name ?? '';

	function getActionDescription(title: string): string {
		if (title === 'device_reboot') {
			return $format('reboot_description', { default: 'Reboot your Shelly device.' });
		}
		if (title === 'factory_reset_ble') {
			return $format('alert_gble_factory_reset_description', {
				values: { type: deviceTypeName },
				default:
					'Resetting your {type} will revert it to its factory settings and delete it from the account.',
			});
		}
		if (
			title === 'factory_reset' ||
			title === 'factory_reset_g1' ||
			title === 'factory_reset_z'
		) {
			return $format('factory_reset_description', {
				values: { type: deviceTypeName },
				default:
					'Resetting your Shelly {type} will revert it to its factory settings. It will disconnect from your Wi-Fi network and all associated users, including you, will be removed from this device.',
			});
		}
		if (title === 'transfer_device') {
			return $format('transfer_device_description_new', {
				default: 'Transfer this device to another account, or move it to one of your other homes.',
			});
		}
		if (title === 'delete_device') {
			if (device.shared) {
				return $format('remove_device_msg', {
					values: { device_name: device.name },
					default:
						"Removing device '{device_name}' will permanently remove it from your account. Access can be restored if the owner shares the device with you again.",
				});
			}
			return $format('delete_device_description', {
				default: 'Permanently remove this device from your account.',
			});
		}
		return '';
	}
</script>

{#if isSharedHomeActive && visibleActionTitles.length > 0}
	<SharedHomeOwnerManagedAlert class="mb-s" context="generic" />
{/if}

{#if showFirmware}
	{#if useNewLayout}
		<SettingGuide
			text={$format('update_firmware_simple_info', {
				default:
					'Updating the firmware will install the latest software version on your device, providing new features, improvements, and bug fixes.',
			})}
		/>
	{:else}
		<Text styleType="body-small" class="text-color-muted mb-m">
			{$format('update_firmware_simple_info', {
				default:
					'Updating the firmware will install the latest software version on your device, providing new features, improvements, and bug fixes.',
			})}
		</Text>
	{/if}
	<FirmwareUpdate {device} {isOffline} compact {useNewLayout} />
	{#if visibleActionTitles.length > 0}
		<hr class="mt-l mb-l" />
	{/if}
{/if}

{#each visibleActionTitles as title, i (title)}
	{@const isFirstOfRebootPair = isWallDisplayRebootPairTitle(title) && title === 'device_reboot'}
	{#if isFirstOfRebootPair}
		{#if useNewLayout}
			<SettingGuide
				text={$format('settings_guide_wall_display_reboot', {
					default: WALL_DISPLAY_REBOOT_GUIDE,
				})}
			/>
		{:else}
			<Text styleType="body-small" class="text-color-muted mb-m">
				{$format('settings_guide_wall_display_reboot', {
					default: WALL_DISPLAY_REBOOT_GUIDE,
				})}
			</Text>
		{/if}
	{:else if !isWallDisplayRebootPairTitle(title)}
		{#if useNewLayout}
			<SettingGuide text={getActionDescription(title)} />
		{:else}
			<Text styleType="body-small" class="text-color-muted mb-m">{getActionDescription(title)}</Text>
		{/if}
	{/if}
	<div class={isFirstOfRebootPair ? 'mb-s' : ''}>
		<svelte:component
			this={ButtonC}
			size="m"
			width="full"
			variant={getActionButtonConfig(title).variant}
			icon={getActionButtonConfig(title).icon}
			disabled={isSharedHomeActive}
			notification={title === 'device_reboot' && rebootPending ? { type: 'danger' } : undefined}
			on:click={() => onActionClick(title)}
		>
			{getActionLabel(title)}
		</svelte:component>
	</div>
	{#if i < visibleActionTitles.length - 1 && !isFirstOfRebootPair}
		<hr class="mt-l mb-l" />
	{/if}
{/each}

{#if showRebootModal}
	<ModalPrompt bind:showModal={showRebootModal} props={rebootProps} />
{/if}

{#if showSoftRebootModal}
	<ModalPrompt bind:showModal={showSoftRebootModal} props={softRebootProps} />
{/if}

{#if showFactoryResetModal}
	<FactoryResetModal bind:showModal={showFactoryResetModal} props={factoryResetProps} />
{/if}

{#if showDeleteModal}
	<DeleteDeviceModal
		bind:showModalDelete={showDeleteModal}
		{device}
		deviceIsUsed={deleteDeviceIsUsed}
		deviceUsage={deleteDeviceUsage}
		on:close={() => {
			showDeleteModal = false;
		}}
	/>
{/if}

{#if showTransferModal}
	<BatchTransferModal bind:showModal={showTransferModal} areFiltersDisabled={false} />
{/if}
